/**
 * Composant d'affichage des erreurs de configuration
 *
 * Utilisé pour présenter les erreurs de manière user-friendly
 * avec un titre, un message explicatif et des détails techniques.
 *
 * @module components/ConfigurationError
 */

import React from 'react';
import PropTypes from 'prop-types';

import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ErrorContainer = styled(Paper)(({theme}) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: 'none',
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'severity',
})(({theme, severity = 'error'}) => ({
  marginBottom: theme.spacing(2),
  // Erreur technique en rouge ; indisponibilité volontaire en teinte neutre atténuée
  color: severity === 'unavailable' ? theme.palette.text.secondary : theme.palette.error.main,
}));

/**
 * Composant réutilisable pour afficher les erreurs de configuration
 *
 * @param {Object} props - Props du composant
 * @param {string} props.title - Titre de l'erreur
 * @param {string} props.message - Message explicatif
 * @param {string} props.details - Détails techniques ou instructions
 * @param {'error'|'unavailable'} [props.severity] - 'error' (défaut) pour une
 *   erreur technique ; 'unavailable' pour une indisponibilité volontaire de
 *   l'établissement (icône neutre, pas de renvoi vers l'administrateur du site)
 * @param {Function} [props.onClose] - Handler de fermeture optionnel
 * @param {boolean} [props.showCloseButton] - Afficher le bouton fermer
 * @return {JSX.Element} Composant d'erreur formaté
 */
export function ConfigurationError({
  title,
  message,
  details,
  severity = 'error',
  onClose,
  showCloseButton = false,
}) {
  const isUnavailable = severity === 'unavailable';
  const StateIcon = isUnavailable ? InfoOutlinedIcon : SentimentDissatisfiedIcon;

  return (
    <ErrorContainer elevation={0}>
      <IconWrapper severity={severity}>
        <StateIcon
          sx={{fontSize: 64}}
          aria-hidden="true"
        />
      </IconWrapper>

      <Typography variant="h5" component="h2" gutterBottom fontWeight="medium">
        {title}
      </Typography>

      <Typography variant="body1" paragraph>
        {message}
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        {details}
      </Typography>

      {/* Le renvoi vers l'administrateur du site suppose un bug : hors de propos
          quand l'établissement a volontairement coupé la vente */}
      {!isUnavailable && (
        <Typography variant="caption" color="text.secondary" sx={{mt: 1}}>
          Si le problème persiste, veuillez contacter l&apos;administrateur du site.
        </Typography>
      )}

      {showCloseButton && (
        <Box sx={{mt: 3}}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            aria-label="Fermer le message d'erreur"
          >
            Fermer
          </Button>
        </Box>
      )}
    </ErrorContainer>
  );
}

ConfigurationError.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  details: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['error', 'unavailable']),
  onClose: PropTypes.func,
  showCloseButton: PropTypes.bool,
};
